import { InvoicePaymentDetails } from './InvoicePaymentDetails';
import React, {useMemo, useState} from 'react';
import {Badge, Button, Card, Modal, Spinner, Table} from "react-bootstrap";
import {useQuery} from "@tanstack/react-query";
import {fetchClientPortalFinancials, fetchClientPortalOpenInvoices, fetchClientPortalUnreconciled} from "../api/clientPortalApi";
import {formatCurrency, formatDate} from "../utils/formatters";
import { isBefore, startOfDay } from 'date-fns';
import {Link} from "react-router-dom";
import * as XLSX from 'xlsx';

function FinancialSection({ partner, rows, creditLimit }) {
    const [selectedPayment, setSelectedPayment] = useState(null);
    const totalAmount = rows.reduce((sum, row) => sum + row.totalAmount.amount, 0);
    const dueAmount = rows.reduce((sum, row) => sum + row.dueAmount.amount, 0);

    const hasCreditLimit = creditLimit && creditLimit.amount !== null;
    const creditLimitLeft = hasCreditLimit ? creditLimit.amount - dueAmount : 0;
    const displayCreditLimitLeft = Math.min(Math.max(0, creditLimitLeft), creditLimit.amount);
    const isOverLimit = hasCreditLimit && creditLimitLeft < 0;

    const today = startOfDay(new Date());

    return (
        <>
            <Modal show={!!selectedPayment} onHide={() => setSelectedPayment(null)}>
                <Modal.Header closeButton><Modal.Title>Payment details</Modal.Title></Modal.Header>
                <Modal.Body>
                    {selectedPayment && <dl className="mb-0">
                        <dt>Company</dt><dd>{selectedPayment.partner}</dd>
                        <dt>Amount</dt><dd>{formatCurrency(selectedPayment.totalAmount)}</dd>
                        <dt>Date</dt><dd>{formatDate(selectedPayment.invoiceDate)}</dd>
                        <dt>Origin / reference</dt><dd>{selectedPayment.reference || 'Not available'}</dd>
                        <dt>Status</dt><dd>Unreconciled payment</dd>
                    </dl>}
                </Modal.Body>
            </Modal>
            <tr className="table-secondary table-group-divider">
                <td colSpan="9" className="fw-bold">
                    <div className="d-flex flex-column flex-lg-row justify-content-between gap-2">
                        <span>{partner}</span>
                        {hasCreditLimit && (
                            <small className="text-body-secondary fw-normal">
                                Credit limit: <b className={isOverLimit ? 'text-danger' : ''}>{formatCurrency({ amount: displayCreditLimitLeft, currency: creditLimit.currency })}</b> / <b>{formatCurrency({ amount: creditLimit.amount, currency: creditLimit.currency })}</b>
                            </small>
                        )}
                    </div>
                </td>
            </tr>
            {rows.map((row) => {
                const isOverdue = row.dueDate && isBefore(new Date(row.dueDate), today);
                return (
                    <tr key={row.name + row.id}>
                        <td><Badge bg={row.type === 'Invoice' ? 'warning' : row.type === 'Credit note' ? 'info' : 'secondary'}>{row.type}</Badge></td>
                        <td>{row.partner}</td>
                        <td>
                            <div className="d-inline-flex align-items-center gap-2">
                                {row.type === 'Payment' ? (
                                    <Button variant="link" size="sm" className="p-0 text-start" onClick={() => setSelectedPayment(row)}>
                                        {row.name}
                                    </Button>
                                ) : <Link to={`/client/invoices/${row.name}`}>{row.name || '-'}</Link>}
                                {row.paymentState === 'partial' && <InvoicePaymentDetails invoiceName={row.name} />}
                                {row.downloadUrl && (
                                    <a href={row.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                        <Badge bg="success">{row.downloadLabel}</Badge>
                                    </a>
                                )}
                                {!row.downloadUrl && row.downloadLabel && (
                                    <Badge bg="secondary" className="opacity-50" title="PDF not available">
                                        {row.downloadLabel}
                                    </Badge>
                                )}
                            </div>
                        </td>
                        <td>
                            {row.invoice_origin && row.invoice_origin.startsWith('SO') ? (
                                <Link to={`/client/orders/${row.invoice_origin}`}>{row.invoice_origin}</Link>
                            ) : (
                                row.invoice_origin || '-'
                            )}
                        </td>
                        <td>{row.reference || '-'}</td>
                        <td>{formatDate(row.invoiceDate)}</td>
                        <td className={isOverdue ? 'text-danger fw-bold' : ''}>
                            {row.dueDate ? formatDate(row.dueDate) : '-'}
                        </td>
                        <td>{formatCurrency(row.totalAmount)}</td>
                        <td>{formatCurrency(row.dueAmount)}</td>
                    </tr>
                );
            })}
            <tr className="table-warning">
                <td colSpan="7" className="text-end fw-semibold">Subtotal</td>
                <td className="fw-semibold">{formatCurrency({ amount: totalAmount, currency: creditLimit.currency })}</td>
                <td className="fw-semibold">{formatCurrency({ amount: dueAmount, currency: creditLimit.currency })}</td>
            </tr>
        </>
    );
}


export function ClientPortalFinancials({ partners }) {
    const { data:open_invoices, isLoading:oi_isLoading, isError:oi_isError, error:oi_error } = useQuery({
        queryKey: ['client-portal-open-invoices'],
        queryFn: fetchClientPortalOpenInvoices,
    });
    const { data:unreconciled, isLoading:ur_isLoading, isError:ur_isError, error:ur_error } = useQuery({
        queryKey: ['client-portal-unreconciled'],
        queryFn: fetchClientPortalUnreconciled,
    });

    let isLoading = oi_isLoading || ur_isLoading;
    let isError = oi_isError || ur_isError;
    let error = oi_error || ur_error;

    const partnerLimitMap = useMemo(() => {
        const map = {};
        (partners || []).forEach(p => {
            map[p.name] = p.credit_limit_legacy;
        });
        return map;
    }, [partners]);

    const financialGroups = useMemo(() => {
        if (!open_invoices?.results && !unreconciled?.results) {
            return [];
        }

        const rows = [];

        // Process invoices
        (open_invoices?.results || []).forEach(inv => {
            let type = 'Invoice';
            if (inv.move_type === 'out_refund') type = 'Credit note';
            if (inv.move_type === 'in_invoice') type = 'Vendor Bill';
            if (inv.move_type === 'in_refund') type = 'Vendor Credit';

            rows.push({
                id: "",
                type: type,
                partner: inv.partner.name,
                name: inv.name,
                paymentState: inv.payment_state,
                invoice_origin: inv.invoice_origin === "False" ? "" : inv.invoice_origin,
                reference: inv.ref === "False" ? "" : inv.ref,
                invoiceDate: inv.invoice_date,
                dueDate: inv.invoice_date_due,
                totalAmount: inv.amount_total_client_signed,
                dueAmount: inv.amount_residual_client_signed,
                downloadUrl: inv.download_link,
                downloadLabel: 'PDF'
            });
        });

        // Process unreconciled payments
        (unreconciled?.results || []).forEach(un => {
            rows.push({
                id: un.mid,
                type: 'Payment',
                partner: un.partner.name,
                name: 'Unreconciled payment',
                reference: un.reference === 'False' ? '' : (un.reference || ''),
                invoiceDate: un.invoice_date,
                dueDate: null,
                totalAmount: {
                    amount: un.credit ? -un.credit.amount : 0,
                    currency: un.credit ? un.credit.currency : 'EUR'
                },
                dueAmount: {
                    amount: un.credit ? -un.credit.amount : 0,
                    currency: un.credit ? un.credit.currency : 'EUR'
                },
                downloadUrl: null,
                downloadLabel: null
            });
        });

        const groupedRows = rows.reduce((groups, item) => {
            if (!groups[item.partner]) {
                groups[item.partner] = [];
            }
            groups[item.partner].push(item);
            return groups;
        }, {});

        return Object.entries(groupedRows).map(([partner, rows]) => {
            const sortedRows = rows.sort((left, right) => {
                const dateA = left.invoiceDate || '0000-00-00';
                const dateB = right.invoiceDate || '0000-00-00';
                return dateA.localeCompare(dateB);
            });

            const currency = sortedRows[0]?.totalAmount.currency || 'EUR';
            const creditLimit = partnerLimitMap[partner] !== undefined ? partnerLimitMap[partner] : null;

            return {
                partner,
                rows: sortedRows,
                creditLimit: creditLimit || { amount: null, currency: currency },
            };
        });
    }, [open_invoices, unreconciled, partnerLimitMap]);

    const grandTotals = useMemo(() => {
        const totals = {};
        financialGroups.forEach((group) => {
            group.rows.forEach((row) => {
                const currency = row.totalAmount.currency;
                if (!totals[currency]) {
                    totals[currency] = { total: 0, due: 0 };
                }
                totals[currency].total += row.totalAmount.amount;
                totals[currency].due += row.dueAmount.amount;
            });
        });
        return Object.entries(totals).map(([currency, values]) => ({
            currency,
            total: values.total,
            due: values.due,
        }));
    }, [financialGroups]);
    const handleExportExcel = () => {
        const exportData = [];

        financialGroups.forEach(group => {
            // Group header (Partner name)
            exportData.push({
                'Type': group.partner,
                'Company': '',
                'Number': '',
                'Origin': '',
                'Reference': '',
                'Invoice Date': '',
                'Due Date': '',
                'Total amount': '',
                'Amount due': ''
            });

            // Rows
            group.rows.forEach(row => {
                exportData.push({
                    'Type': row.type,
                    'Company': row.partner,
                    'Number': row.name || '',
                    'Origin': row.invoice_origin || '',
                    'Reference': row.reference || '',
                    'Invoice Date': formatDate(row.invoiceDate),
                    'Due Date': row.dueDate ? formatDate(row.dueDate) : '',
                    'Total amount': row.totalAmount.amount,
                    'Amount due': row.dueAmount.amount,
                    'Currency': row.totalAmount.currency
                });
            });

            // Subtotal
            const subtotalAmount = group.rows.reduce((sum, row) => sum + row.totalAmount.amount, 0);
            const subtotalDue = group.rows.reduce((sum, row) => sum + row.dueAmount.amount, 0);
            const currency = group.rows[0]?.totalAmount.currency || '';

            exportData.push({
                'Type': 'Subtotal',
                'Company': '',
                'Number': '',
                'Origin': '',
                'Reference': '',
                'Invoice Date': '',
                'Due Date': '',
                'Total amount': subtotalAmount,
                'Amount due': subtotalDue,
                'Currency': currency
            });
            
            // Empty row for spacing
            exportData.push({});
        });

        // Grand totals
        exportData.push({ 'Type': 'Grand total - ALL COMPANIES' });
        grandTotals.forEach(gt => {
            exportData.push({
                'Type': 'Total',
                'Currency': gt.currency,
                'Total amount': gt.total,
                'Amount due': gt.due
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Finances");

        // Set column widths
        const wscols = [
            {wch: 20}, // Type
            {wch: 30}, // Company
            {wch: 20}, // Number
            {wch: 15}, // Origin
            {wch: 20}, // Reference
            {wch: 15}, // Invoice Date
            {wch: 15}, // Due Date
            {wch: 15}, // Total amount
            {wch: 15}, // Amount due
            {wch: 10}, // Currency
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, "finances.xlsx");
    };

    return (
        <Card>
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-white py-3">
                <h5 className="mb-0">Finances</h5>
                <small className="text-muted ms-auto text-end">Payments may take 1–2 days to appear in your balance and invoice status.</small>
                {!isLoading && !isError && financialGroups.length > 0 && (
                    <Button variant="outline-primary" size="sm" onClick={handleExportExcel}>
                        Export to Excel
                    </Button>
                )}
            </Card.Header>
            {isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table responsive className="card-body table transaction-table mb-0">
                    <thead>
                    <tr>
                        <th>Type</th>
                        <th>Company</th>
                        <th>Number</th>
                        <th>Origin</th>
                        <th>Reference</th>
                        <th>Invoice Date</th>
                        <th>Due Date</th>
                        <th>Total amount</th>
                        <th>Amount due</th>
                    </tr>
                    </thead>
                    <tbody>
                    {financialGroups.map((group) => (
                        <React.Fragment key={group.partner}>
                            <FinancialSection
                                partner={group.partner}
                                rows={group.rows}
                                creditLimit={group.creditLimit}
                            />
                            <tr style={{ height: '40px' }}>
                                <td colSpan="9" className="p-0 bg-light"></td>
                            </tr>
                        </React.Fragment>
                    ))}
                    <tr>
                        <td colSpan="9" className="fw-semibold text-center table-secondary table-group-divider py-1">Grand total - ALL COMPANIES</td>
                    </tr>
                    {grandTotals.map((grandTotal) => (
                        <tr key={grandTotal.currency} className="table-primary">
                            <td colSpan="7" className="text-end">{grandTotal.currency}</td>
                            <td className="fw-semibold">
                                {formatCurrency({
                                    amount: grandTotal.total,
                                    currency: grandTotal.currency,
                                })}
                            </td>
                            <td className="fw-semibold">
                                {formatCurrency({
                                    amount: grandTotal.due,
                                    currency: grandTotal.currency,
                                })}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            )}
        </Card>
    );
}
